import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { SERVICES, CATEGORIES } from "@/data/services";
import { ServiceDetail } from "./ServiceDetail";
import { SectionHead } from "./SectionHead";

const findService = (id) => SERVICES.find((s) => s.id === id) || null;
const filterByCategory = (cat) => (cat === "all" ? SERVICES : SERVICES.filter((s) => s.category === cat));
const scrollToRegister = () => document.getElementById("services")?.scrollIntoView({ block: "start" });

const ROW_INITIAL = { opacity: 0, y: 12 };
const ROW_INVIEW = { opacity: 1, y: 0 };
const ROW_VIEWPORT = { once: true, margin: "-40px" };
const ROW_T = { duration: 0.45 };

const CAT_SHORT = {
  risk: "PHA & Risk",
  compliance: "Compliance",
  systems: "Engineering",
  "process-eng": "Process Eng.",
  dust: "Dust Hazards",
};

export const Services = ({ onInquire }) => {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const [active, setActive] = useState("all");

  // The open service is driven by the URL (/services/:id) so every service is deep-linkable.
  const selected = findService(serviceId);

  useEffect(() => {
    if (serviceId && !selected) navigate("/", { replace: true });
    if (selected) scrollToRegister();
  }, [serviceId, selected, navigate]);

  const openService = (s) => navigate(`/services/${s.id}`);
  const closeService = () => navigate("/", { replace: true });

  const filtered = filterByCategory(active);

  return (
    <section id="services" data-testid="services-section" className="py-20 lg:py-24 bg-[#F4F6F3]">
      <div className="mx-auto max-w-[1180px] px-5 lg:px-8">
        <SectionHead
          no="01"
          label="Capabilities"
          title="The service register"
          sub="Full-lifecycle process safety, risk analysis, and mechanical integrity engineering — from hazard identification through consequence modeling and facility siting. Filter by discipline or scan the full register."
        />

        <div className="flex flex-wrap gap-2" data-testid="services-filter">
          {CATEGORIES.map((c) => (
            <button
              key={c.id}
              data-testid={`filter-${c.id}`}
              onClick={() => setActive(c.id)}
              className={`font-body text-[13.5px] font-medium px-4 py-[9px] rounded-[2px] border transition-colors ${
                active === c.id
                  ? "bg-[#04162E] text-white border-[#04162E]"
                  : "bg-white text-[#5C6C80] border-[rgba(4,22,46,0.14)] hover:border-[#8FA0B3]"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>

        <div className="mt-7 border-t border-[rgba(4,22,46,0.14)]" data-testid="services-register">
          {filtered.map((s) => (
            <motion.button
              key={s.id}
              initial={ROW_INITIAL}
              whileInView={ROW_INVIEW}
              viewport={ROW_VIEWPORT}
              transition={ROW_T}
              onClick={() => openService(s)}
              data-testid={`service-card-${s.id}`}
              className="group w-full text-left grid grid-cols-[44px_1fr] md:grid-cols-[60px_1fr_230px] gap-x-4 md:gap-x-7 gap-y-2 py-[26px] border-b border-[rgba(4,22,46,0.14)] hover:bg-[rgba(11,107,68,0.04)] transition-colors"
            >
              <span className="font-body text-xs font-semibold text-[#0B6B44] tracking-[0.02em] pt-1 row-span-3 md:row-span-1">
                {s.code}
              </span>
              <div>
                <h3 className="font-display text-[19px] font-bold text-[#04162E] mb-2 group-hover:text-[#0B6B44] transition-colors">
                  {s.title}
                </h3>
                <p className="font-body text-[14.5px] text-[#5C6C80] leading-[1.6] max-w-[52ch]">{s.short}</p>
              </div>
              <div className="md:text-right col-start-2 md:col-start-3">
                <div className="font-body text-[12.5px] text-[#8FA0B3]">{s.standard}</div>
                <div className="font-body text-[11px] font-semibold text-[#0B6B44] mt-2">{CAT_SHORT[s.category]}</div>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      <ServiceDetail
        service={selected}
        onClose={closeService}
        onInquire={(svc) => {
          closeService();
          onInquire?.(svc);
        }}
      />
    </section>
  );
};
